
import React from 'react';
import { motion } from 'framer-motion';
import { Users, Video, MessageSquare, ArrowRight, Sparkles, Shield, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useAuraPoints } from '@/contexts/AuraPointsContext';
import AuraPointsSummary from './AuraPointsSummary';
import { createGlassEffect } from '@/utils/ui-effects';

interface MobileLandingViewProps {
  stats: {
    onlineUsers: number;
    activeRooms: number;
    totalChats: number;
  };
}

const MobileLandingView: React.FC<MobileLandingViewProps> = ({ stats }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const { points } = useAuraPoints();
  const isDark = theme === 'dark';

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 10 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Float animation for subtle background elements - fixed to use proper Framer Motion types
  const floatAnimation = {
    initial: { y: 0 },
    animate: { 
      y: [0, -10, 0],
      transition: {
        duration: 3,
        repeat: Infinity,
        repeatType: "mirror" as const,
        ease: "easeInOut"
      }
    }
  };

  return (
    <div className={cn(
      "flex flex-col min-h-screen relative overflow-hidden",
      isDark ? "bg-[#0a0e1e]" : "bg-white"
    )}>
      {/* Floating background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div 
            key={i}
            className={cn(
              "absolute rounded-full opacity-20",
              isDark ? "bg-blue-500/30" : "bg-blue-500/10"
            )}
            style={{
              width: Math.random() * 100 + 50,
              height: Math.random() * 100 + 50,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            initial="initial"
            animate="animate"
            variants={floatAnimation}
            transition={{
              delay: Math.random() * 2,
              duration: Math.random() * 3 + 2
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className={cn(
        "px-5 py-10 flex flex-col items-center text-center relative z-10",
        isDark ? "bg-[#0a0e1e]" : "bg-white"
      )}>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerChildren}
          className="w-full max-w-md mx-auto"
        >
          {/* Blue Pill Badge */}
          <motion.div 
            variants={fadeIn}
            className={cn(
              "inline-block py-2 px-5 rounded-full mb-6 text-blue-600",
              isDark 
                ? "bg-blue-500/10" 
                : "bg-blue-100"
            )}
          >
            <span className="text-sm font-medium">Connect Instantly, Chat Freely</span>
          </motion.div>
          
          {/* Main Heading */}
          <motion.h1 
            variants={fadeIn}
            className={cn(
              "text-4xl font-bold tracking-tight mb-2",
              isDark ? "text-white" : "text-slate-900"
            )}
          >
            Meet New People
          </motion.h1>
          
          {/* Gradient Subtitle */}
          <motion.h2
            variants={fadeIn}
            className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-violet-500 bg-clip-text text-transparent"
          >
            From Anywhere, Anytime
          </motion.h2>
          
          {/* Description */}
          <motion.p 
            variants={fadeIn}
            className={cn(
              "text-base mb-6",
              isDark ? "text-slate-300" : "text-slate-600"
            )}
          >
            Chatrix connects you with interesting people worldwide in just seconds.
          </motion.p>
          
          {/* CTA Button */}
          <motion.div 
            variants={fadeIn}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="w-full"
          >
            <Button 
              onClick={() => navigate('/chat')}
              className={cn(
                "rounded-full px-8 py-6 h-auto w-full md:w-auto font-medium text-base",
                "bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white",
                "shadow-lg", isDark ? "shadow-blue-900/30" : "shadow-blue-500/20"
              )}
            >
              Start Video Chat
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="px-5 py-8">
        <div className="grid grid-cols-3 gap-2 max-w-md mx-auto">
          {/* Online Users Stat */}
          <motion.div 
            className="col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card className={cn(
              "overflow-hidden border-0 h-full",
              isDark ? "bg-[#151830] shadow-lg shadow-black/10" : "bg-white shadow-md shadow-blue-500/5"
            )}>
              <div className="p-4 flex flex-col items-center text-center h-full">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                  "bg-gradient-to-br from-green-400 to-green-600 text-white"
                )}>
                  <Users className="h-5 w-5" />
                </div>
                <p className={cn(
                  "text-xl font-bold",
                  isDark ? "text-white" : "text-slate-900"
                )}>
                  {stats.onlineUsers.toLocaleString()}
                </p>
                <p className={cn(
                  "text-xs",
                  isDark ? "text-slate-400" : "text-slate-500"
                )}>
                  Online Now
                </p>
              </div>
            </Card>
          </motion.div>
          
          {/* Active Rooms Stat */}
          <motion.div 
            className="col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card className={cn(
              "overflow-hidden border-0 h-full",
              isDark ? "bg-[#151830] shadow-lg shadow-black/10" : "bg-white shadow-md shadow-blue-500/5"
            )}>
              <div className="p-4 flex flex-col items-center text-center h-full">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                  "bg-gradient-to-br from-blue-400 to-blue-600 text-white"
                )}>
                  <Video className="h-5 w-5" />
                </div>
                <p className={cn(
                  "text-xl font-bold",
                  isDark ? "text-white" : "text-slate-900"
                )}>
                  {stats.activeRooms.toLocaleString()}
                </p>
                <p className={cn(
                  "text-xs",
                  isDark ? "text-slate-400" : "text-slate-500"
                )}>
                  Active Rooms
                </p>
              </div>
            </Card>
          </motion.div>
          
          {/* Total Chats Stat */}
          <motion.div 
            className="col-span-1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className={cn(
              "overflow-hidden border-0 h-full",
              isDark ? "bg-[#151830] shadow-lg shadow-black/10" : "bg-white shadow-md shadow-blue-500/5"
            )}>
              <div className="p-4 flex flex-col items-center text-center h-full">
                <div className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center mb-2",
                  "bg-gradient-to-br from-violet-400 to-violet-600 text-white"
                )}>
                  <MessageSquare className="h-5 w-5" />
                </div>
                <p className={cn(
                  "text-xl font-bold",
                  isDark ? "text-white" : "text-slate-900"
                )}>
                  {stats.totalChats.toLocaleString()}
                </p>
                <p className={cn(
                  "text-xs",
                  isDark ? "text-slate-400" : "text-slate-500"
                )}>
                  Chats Today
                </p>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Points Summary (if logged in) */}
      {currentUser && (
        <motion.section
          className="py-4 px-5"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <AuraPointsSummary minimal={true} />
        </motion.section>
      )}

      {/* Features Section */}
      <section className="px-5 py-8">
        <motion.h2 
          className={cn(
            "text-xl font-bold mb-6 text-center",
            isDark ? "text-white" : "text-slate-900"
          )}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          Why Choose Chatrix
        </motion.h2>

        <div className="space-y-4 max-w-md mx-auto">
          {/* Feature 1 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            <Card className={cn(
              "border overflow-hidden",
              isDark 
                ? "bg-[#151830] border-[#252a47] hover:border-blue-500/30" 
                : "bg-white border-slate-200/50 shadow-sm hover:border-blue-300/50"
            )}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "mt-1 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                    "bg-gradient-to-br from-blue-400 to-blue-600 text-white"
                  )}>
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={cn(
                      "font-semibold mb-1",
                      isDark ? "text-white" : "text-slate-900"
                    )}>
                      Connect Instantly
                    </h3>
                    <p className={cn(
                      "text-sm",
                      isDark ? "text-slate-300" : "text-slate-600"
                    )}>
                      Find and chat with people around the world with one click.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Feature 2 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            <Card className={cn(
              "border overflow-hidden",
              isDark 
                ? "bg-[#151830] border-[#252a47] hover:border-violet-500/30" 
                : "bg-white border-slate-200/50 shadow-sm hover:border-violet-300/50"
            )}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "mt-1 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                    "bg-gradient-to-br from-violet-400 to-violet-600 text-white"
                  )}>
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={cn(
                      "font-semibold mb-1",
                      isDark ? "text-white" : "text-slate-900"
                    )}>
                      Privacy First
                    </h3>
                    <p className={cn(
                      "text-sm",
                      isDark ? "text-slate-300" : "text-slate-600"
                    )}>
                      End-to-end encryption and automatic message deletion for your security.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
          
          {/* Feature 3 */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            whileHover={{ y: -5, scale: 1.02 }}
          >
            <Card className={cn(
              "border overflow-hidden",
              isDark 
                ? "bg-[#151830] border-[#252a47] hover:border-green-500/30" 
                : "bg-white border-slate-200/50 shadow-sm hover:border-green-300/50"
            )}>
              <div className="p-4">
                <div className="flex items-start gap-3">
                  <div className={cn(
                    "mt-1 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                    "bg-gradient-to-br from-green-400 to-green-600 text-white"
                  )}>
                    <Globe className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className={cn(
                      "font-semibold mb-1",
                      isDark ? "text-white" : "text-slate-900"
                    )}>
                      Global Connections
                    </h3>
                    <p className={cn(
                      "text-sm",
                      isDark ? "text-slate-300" : "text-slate-600"
                    )}>
                      Meet people from different cultures with real-time translation.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Section - Matching desktop version */}
      <section className={cn(
        "mt-auto py-10 px-5 text-center relative z-10",
        "bg-gradient-to-r from-blue-500/10 to-violet-500/10 backdrop-blur-sm"
      )}>
        <div className="max-w-md mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <h2 className={cn(
              "text-xl font-bold mb-3",
              isDark ? "text-white" : "text-slate-900"
            )}>
              Ready to Start Chatting?
            </h2>
            <p className={cn(
              "mb-6 text-sm opacity-80",
              isDark ? "text-white/80" : "text-slate-700"
            )}>
              Jump into live video conversations with interesting people from around the world
            </p>
            <div className="space-y-3">
              <motion.div
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  onClick={() => navigate('/chat')}
                  className={cn(
                    "w-full rounded-full px-6 py-6 h-auto text-base",
                    "bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white shadow-xl",
                    isDark ? "shadow-blue-900/30" : "shadow-blue-500/20"
                  )}
                >
                  Start Now
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </motion.div>
              
              {!currentUser && (
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/login')}
                    className={cn(
                      "w-full rounded-full px-6 py-6 h-auto text-base",
                      isDark 
                        ? "bg-transparent border-white/20 hover:bg-white/10 text-white" 
                        : "bg-white border-slate-200 text-slate-800"
                    )}
                  >
                    Log In or Sign Up
                  </Button>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MobileLandingView;
